import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfig } from '@lore/config';

/**
 * @typedef {Object} ProjectImage
 * @property {string} imageUrl
 * @property {number} projectId
 * @property {number} width
 * @property {number} height
 */

/**
 * Custom hook for the share image of a project.
 * GET /images/project/{projectId} — `image` is null while loading or when the
 * project has no saved image yet (404 is treated as "no image"). Runs
 * against the `community` connection, since that GET only exists on
 * community_v3.
 * upsert() POSTs /images/upsert on the `default` connection (with
 * bypassUserScope=true, since steward-ui users are rarely the project's
 * creator) and refreshes the query on success.
 *
 * @param {number} projectId
 * @returns {import('@tanstack/react-query').UseQueryResult & { image: ProjectImage|null, upsert: Function, upserting: boolean, upsertError: boolean }}
 */
export default function useProjectImage(projectId) {
  const config = useConfig();
  const { apiRoot: communityApiRoot, headers: communityHeaders } = config.connections.community;
  const { apiRoot: defaultApiRoot, headers: defaultHeaders } = config.connections.default;
  const queryClient = useQueryClient();

  const queryKey = ['images', 'project', projectId];

  const query = useQuery({
    queryKey,
    queryFn: () => {
      const url = `${communityApiRoot}/images/project/${projectId}`;
      return fetch(url, {
        method: 'GET',
        headers: communityHeaders(),
      }).then((res) => {
        if (res.status === 404) {
          return null;
        }
        if (!res.ok) {
          throw new Error('Failed to fetch the project image');
        }
        return res.json();
      });
    },
    enabled: Boolean(projectId),
  });

  const upsertMutation = useMutation({
    mutationFn: ({ imageUrl, width, height }) => {
      const url = `${defaultApiRoot}/images/upsert?bypassUserScope=true`;
      return fetch(url, {
        method: 'POST',
        headers: {
          ...defaultHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ imageUrl, projectId, width, height }),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to save the project image');
        }
        return res.json();
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });

  return {
    ...query,
    image: query.data ?? null,

    // Actions
    upsert: ({ imageUrl, width, height }) => upsertMutation.mutate({ imageUrl, width, height }),
    upserting: upsertMutation.isLoading,
    upsertError: upsertMutation.isError,
  };
}
